/*eslint-disable i18next/no-literal-string*/
import React, { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import ExclamationIcon from "@/inline-assets/exclamation-mark.svg";
import LoadingSpinner from "@/inline-assets/loading-spinner.svg";
import matchRefs from "@/shared/Match.refs.jsx";
import {
  MatchHeaderContainer,
  MatchLayoutContainer,
  MatchLink,
  MatchTileEmptyContainer,
} from "@/shared/Match.style.jsx";
import type { PageHeaderProps } from "@/shared/PageHeader.jsx";
import PageHeader from "@/shared/PageHeader.jsx";
import type { BadgeProps } from "@/shared/PostMatchBadge.jsx";
import PostMatchBadgeList from "@/shared/PostMatchBadgeList.jsx";

export function MatchLoading({ height }) {
  return (
    <MatchTileEmptyContainer style={{ height: height || "7.875rem" }}>
      <LoadingSpinner />
    </MatchTileEmptyContainer>
  );
}

export function MatchError({ ErrorMessage }) {
  const { t } = useTranslation();

  return (
    <MatchTileEmptyContainer className="flex column">
      <ExclamationIcon />
      {ErrorMessage ? (
        <ErrorMessage />
      ) : (
        <>
          <span className="type-article-headline">
            {t(
              "common:postmatch.matchMissing",
              "This match is missing somewhere in the dark world...",
            )}
          </span>
          <span className="type-body2">
            {t("common:postmatch.checkMatchLater", "Please check back later.")}
          </span>
        </>
      )}
    </MatchTileEmptyContainer>
  );
}

export function Match(props) {
  const {
    children,
    match,
    handleLoading = true,
    height,
    matchRoute,
    ErrorMessage,
    handleMouseEvents = false,
  } = props;
  const matchError = match instanceof Error ? match : null;
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    matchRefs.setupPlaceholder(containerRef.current);
  }, [match, props.height]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MatchLayoutContainer ref={containerRef}>
      {matchError ? (
        <MatchError ErrorMessage={ErrorMessage} />
      ) : !match && handleLoading ? (
        <MatchLoading height={height} />
      ) : matchRoute ? (
        <MatchLink href={matchRoute}>
          {handleMouseEvents ? children : ""}
        </MatchLink>
      ) : (
        children
      )}
    </MatchLayoutContainer>
  );
}

export function MatchHeader({ children }) {
  return (
    <div className={`${MatchHeaderContainer()} match-header`}>
      <div className="left">{children}</div>
      <div className="right" />
    </div>
  );
}

export const SharedMatchLayout = ({
  hideHeader,
  match,
  matchRoute,
  children,
  badges,
  handleLoading,
  ErrorMessage,
  ...headerProps
}: PageHeaderProps & {
  hideHeader?: boolean;
  match: unknown;
  matchRoute?: string;
  children: unknown;
  badges?: BadgeProps[];
  handleLoading?: boolean;
  ErrorMessage?: React.ReactNode;
}): React.JSX.Element => {
  return (
    <Match
      match={match}
      matchRoute={matchRoute}
      handleLoading={handleLoading}
      ErrorMessage={ErrorMessage}
    >
      {!hideHeader && (
        <MatchHeader>
          <PageHeader
            imageRound
            aside={
              badges?.length ? (
                <PostMatchBadgeList size="medium" max={3} list={badges} />
              ) : null
            }
            {...headerProps}
          />
        </MatchHeader>
      )}
      {children}
    </Match>
  );
};
